import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { verifyMfa, requestMfaChallenge, useBackupCode } from '../MfaApi';

const SUPPORT_EMAIL = 'support@videri.com';

const btnStyle = { bgcolor: '#01070B', '&:hover': { bgcolor: '#2c2c2c' } };
const colSx = {
  display: 'flex', flexDirection: 'column', gap: 2, pt: 1,
};
const linkStyle = {
  background: 'none',
  border: 'none',
  padding: 0,
  color: '#0026A7',
  cursor: 'pointer',
  textDecoration: 'underline',
  font: 'inherit',
};
const subtleLinkStyle = {
  ...linkStyle,
  color: '#666',
};

function MfaChallengeModal({
  token, method, enrolledMethods, onVerified, onLockedOut, onSignOut,
}) {
  const [activeMethod, setActiveMethod] = useState(method);
  const [code, setCode] = useState('');
  const [backupMode, setBackupMode] = useState(false);
  const [error, setError] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(method !== 'totp' ? 30 : 0);
  const challengeSentRef = useRef(false);

  useEffect(() => {
    if (!backupMode) {
      setCode('');
      setError(null);
      challengeSentRef.current = false;
    }
  }, [backupMode]);

  const handleSwitchMethod = async (newMethod) => {
    setCode('');
    setError(null);
    setActiveMethod(newMethod);
    if (newMethod === 'email') {
      setResendCountdown(30);
      await requestMfaChallenge(token).catch(() => {});
    } else {
      setResendCountdown(0);
    }
  };

  useEffect(() => {
    if (resendCountdown <= 0) return undefined;
    const timer = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) { clearInterval(timer); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCountdown]);

  const handleVerify = async () => {
    if (!code) return;
    setVerifying(true);
    setError(null);
    try {
      await verifyMfa(token, code, activeMethod);
      onVerified();
    } catch (err) {
      if (err.status === 423) {
        onLockedOut();
      } else if (err.code === 'INVALID_CODE') {
        const suffix = err.attemptsRemaining != null
          ? ` ${err.attemptsRemaining} attempt(s) remaining.`
          : '';
        setError(`Incorrect code.${suffix}`);
      } else {
        setError('Verification failed. Please try again.');
      }
      setVerifying(false);
    }
  };

  const canSwitchToTotp = enrolledMethods.includes('totp') && activeMethod === 'email';
  const canSwitchToEmail = enrolledMethods.includes('email') && activeMethod === 'totp';

  const handleResend = async () => {
    setResendLoading(true);
    try {
      await requestMfaChallenge(token);
      challengeSentRef.current = true;
    } catch {
      // non-blocking
    } finally {
      setResendCountdown(30);
      setResendLoading(false);
    }
  };

  const handleBackupSubmit = async () => {
    if (!code) return;
    setVerifying(true);
    setError(null);
    try {
      await useBackupCode(token, code);
      onVerified();
    } catch (err) {
      if (err.code === 'INVALID_CODE') {
        setError('Invalid backup code. Please try again.');
      } else {
        setError('Something went wrong. Please try again.');
      }
      setVerifying(false);
    }
  };

  return (
    <Dialog open disableEscapeKeyDown maxWidth="xs" fullWidth>
      <DialogTitle>
        <Typography component="span" sx={{ fontWeight: 700, fontSize: '22px' }}>
          Multi-Factor Authentication
        </Typography>
      </DialogTitle>
      <DialogContent>
        {backupMode ? (
          <Box sx={colSx}>
            <Typography variant="body2" color="text.secondary">
              Enter one of the backup codes you saved when you set up multi-factor authentication.
            </Typography>
            <TextField
              label="Backup code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^a-zA-Z0-9]/g, ''))}
              fullWidth
              error={!!error}
              helperText={error ?? ' '}
              inputProps={{ 'data-testid': 'mfa-backup-code-input' }}
            />
            <Button
              variant="contained"
              onClick={handleBackupSubmit}
              disabled={verifying || !code}
              data-testid="mfa-backup-submit-btn"
              sx={btnStyle}
            >
              {verifying ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Continue'}
            </Button>
            <Typography variant="body2" textAlign="center">
              <button
                type="button"
                style={linkStyle}
                data-testid="mfa-back-to-verification-btn"
                onClick={() => setBackupMode(false)}
              >
                Back to verification code
              </button>
            </Typography>
          </Box>
        ) : (
          <Box sx={colSx}>
            <Typography variant="body2" color="text.secondary">
              {activeMethod === 'totp'
                ? 'Enter the 6-digit code from your authenticator app.'
                : 'Enter the code we sent to your email.'}
            </Typography>
            <TextField
              label="6-digit code"
              type="tel"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              fullWidth
              error={!!error}
              helperText={error ?? ' '}
              inputProps={{ maxLength: 6, 'data-testid': 'mfa-challenge-code-input' }}
              onKeyDown={(e) => { if (e.key === 'Enter') handleVerify(); }}
            />
            <Button
              variant="contained"
              onClick={handleVerify}
              disabled={verifying || code.length < 6}
              data-testid="mfa-challenge-submit-btn"
              sx={btnStyle}
            >
              {verifying ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Verify'}
            </Button>
            <Typography variant="body2" textAlign="center">
              {activeMethod !== 'totp' && (
                <>
                  Didn&apos;t receive it?&nbsp;
                  <button
                    type="button"
                    style={resendCountdown > 0 ? subtleLinkStyle : linkStyle}
                    data-testid="mfa-challenge-resend-btn"
                    onClick={handleResend}
                    disabled={resendLoading || resendCountdown > 0}
                  >
                    {resendLoading ? 'Sending…' : `Resend${resendCountdown > 0 ? ` (${resendCountdown}s)` : ''}`}
                  </button>
                  &nbsp;·&nbsp;
                </>
              )}
              <a href={`mailto:${SUPPORT_EMAIL}`}>Contact support</a>
            </Typography>
            <Typography variant="body2" textAlign="center">
              <button
                type="button"
                style={linkStyle}
                data-testid="mfa-use-backup-code-btn"
                onClick={() => setBackupMode(true)}
              >
                Use a backup code
              </button>
            </Typography>
            {canSwitchToTotp && (
              <Typography variant="body2" textAlign="center">
                <button
                  type="button"
                  style={linkStyle}
                  data-testid="mfa-switch-to-totp-btn"
                  onClick={() => handleSwitchMethod('totp')}
                >
                  Use authenticator app instead
                </button>
              </Typography>
            )}
            {canSwitchToEmail && (
              <Typography variant="body2" textAlign="center">
                <button
                  type="button"
                  style={linkStyle}
                  data-testid="mfa-switch-to-email-btn"
                  onClick={() => handleSwitchMethod('email')}
                >
                  Use email instead
                </button>
              </Typography>
            )}
            <Typography variant="body2" textAlign="center">
              <button type="button" style={linkStyle} data-testid="mfa-sign-out-btn" onClick={onSignOut}>
                Sign out
              </button>
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

MfaChallengeModal.propTypes = {
  token: PropTypes.string.isRequired,
  method: PropTypes.string,
  enrolledMethods: PropTypes.arrayOf(PropTypes.string),
  onVerified: PropTypes.func.isRequired,
  onLockedOut: PropTypes.func.isRequired,
  onSignOut: PropTypes.func.isRequired,
};

MfaChallengeModal.defaultProps = {
  method: 'email',
  enrolledMethods: [],
};

export default MfaChallengeModal;
